import { useState } from "react";
import { useParams, Link, useLocation, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowLeft, MapPin, Briefcase, Clock, DollarSign } from "lucide-react";
import { careersAPI } from "../../services/api";
import CareersApplyForm from "./CareersApplyForm";
import Seo from "../../components/Seo";

const VALID_SOURCES = ["linkedin", "indeed", "direct", "internal", "referral"];

export default function CareersJobDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [showForm, setShowForm] = useState(location.pathname.endsWith("/apply"));
  const [applied, setApplied] = useState(null);

  const rawSource = searchParams.get("source")?.toLowerCase();
  const source = VALID_SOURCES.includes(rawSource) ? rawSource : "direct";

  const {
    data: posting,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["careers-posting", id],
    queryFn: () => careersAPI.getPosting(id).then((r) => r.data),
  });

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="h-8 w-64 rounded-lg animate-pulse" style={{ backgroundColor: "var(--lm-s1)" }} />
        <div className="h-4 w-full rounded animate-pulse" style={{ backgroundColor: "var(--lm-s1)" }} />
        <div className="h-4 w-3/4 rounded animate-pulse" style={{ backgroundColor: "var(--lm-s1)" }} />
      </div>
    );
  }

  if (isError || !posting) {
    return (
      <div className="py-20 text-center space-y-4">
        <p style={{ color: "var(--lm-ink-3)" }}>{t("careers.detail.notFound")}</p>
        <Link to="/careers" className="text-sm" style={{ color: "#5e6ad2" }}>
          {t("careers.detail.backAll")}
        </Link>
      </div>
    );
  }

  if (applied) {
    return <SuccessScreen token={applied} jobTitle={posting.title} t={t} />;
  }

  const seoTitle = posting.title ? `${posting.title} — Mawered SIRH` : "Offre d'emploi — Mawered SIRH";
  const seoDesc = posting.description
    ? posting.description.replace(/<[^>]+>/g, "").slice(0, 160)
    : "Postulez à cette offre d'emploi chez Mawered.";

  const jobPostingJsonLd = {
    "@context": "https://schema.org",
    "@type": "JobPosting",
    title: posting.title,
    description: posting.description ? posting.description.replace(/<[^>]+>/g, "") : "",
    ...(posting.created_at ? { datePosted: posting.created_at.split("T")[0] } : {}),
    ...(posting.expires_at ? { validThrough: posting.expires_at.split("T")[0] } : {}),
    ...(posting.contract_type ? { employmentType: posting.contract_type.toUpperCase().replace(/\s+/g, "_") } : {}),
    hiringOrganization: {
      "@type": "Organization",
      name: "Mawered",
      sameAs: "https://mawered.com",
    },
    ...(posting.country_code
      ? {
          jobLocation: {
            "@type": "Place",
            address: {
              "@type": "PostalAddress",
              addressCountry: posting.country_code,
            },
          },
        }
      : {}),
    ...(posting.salary_min || posting.salary_max
      ? {
          baseSalary: {
            "@type": "MonetaryAmount",
            currency: posting.currency || "TND",
            value: {
              "@type": "QuantitativeValue",
              ...(posting.salary_min ? { minValue: Number(posting.salary_min) } : {}),
              ...(posting.salary_max ? { maxValue: Number(posting.salary_max) } : {}),
              unitText: "MONTH",
            },
          },
        }
      : {}),
  };

  return (
    <>
      <Seo title={seoTitle} description={seoDesc} path={`/careers/${id}`} type="article" jsonLd={jobPostingJsonLd} />
      <div className="space-y-8">
        {/* Back */}
        <Link
          to="/careers"
          className="inline-flex items-center gap-2 text-xs no-underline transition-colors"
          style={{ color: "var(--lm-ink-3)" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.color = "var(--lm-ink)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.color = "var(--lm-ink-3)";
          }}
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t("careers.detail.allOffers")}
        </Link>

        {/* Header */}
        <div className="space-y-4">
          <h1
            className="text-3xl font-semibold"
            style={{ color: "var(--lm-ink)", letterSpacing: "-0.7px", lineHeight: 1.15 }}
          >
            {posting.title}
          </h1>

          <div className="flex flex-wrap gap-4">
            {posting.contract_type && <Meta icon={Briefcase} label={posting.contract_type} />}
            {posting.country_code && <Meta icon={MapPin} label={posting.country_code} />}
            {posting.experience_required && <Meta icon={Clock} label={posting.experience_required} />}
            {(posting.salary_min || posting.salary_max) && (
              <Meta
                icon={DollarSign}
                label={`${posting.salary_min ? Number(posting.salary_min).toLocaleString() : ""}${posting.salary_max ? ` — ${Number(posting.salary_max).toLocaleString()}` : ""} ${posting.currency}`}
              />
            )}
          </div>

          <button
            onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all"
            style={{ backgroundColor: "#5e6ad2", color: "#fff", border: "1px solid #5e6ad2" }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#828fff";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "#5e6ad2";
            }}
          >
            {t("careers.detail.apply")}
          </button>
        </div>

        <div style={{ height: "1px", backgroundColor: "var(--lm-border)" }} />

        {/* Description */}
        <Section title={t("careers.detail.descriptionTitle")} content={posting.description} />

        {posting.requirements && (
          <Section title={t("careers.detail.requirementsTitle")} content={posting.requirements} />
        )}

        {/* Apply form inline */}
        {showForm && (
          <>
            <div style={{ height: "1px", backgroundColor: "var(--lm-border)" }} />
            <div>
              <h2 className="text-lg font-semibold mb-6" style={{ color: "var(--lm-ink)", letterSpacing: "-0.3px" }}>
                {t("careers.detail.yourApplication")}
              </h2>
              <CareersApplyForm
                postingId={parseInt(id)}
                source={source}
                onSuccess={(token) => setApplied(token)}
                onCancel={() => setShowForm(false)}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}

function Meta({ icon: Icon, label }) {
  return (
    <span className="flex items-center gap-1.5 text-sm" style={{ color: "var(--lm-ink-2)" }}>
      <Icon className="h-4 w-4" style={{ color: "var(--lm-ink-3)" }} />
      {label}
    </span>
  );
}

function Section({ title, content }) {
  return (
    <div className="space-y-3">
      <h2 className="text-base font-semibold" style={{ color: "var(--lm-ink)", letterSpacing: "-0.2px" }}>
        {title}
      </h2>
      <div className="text-sm leading-relaxed whitespace-pre-wrap" style={{ color: "var(--lm-ink-2)" }}>
        {content}
      </div>
    </div>
  );
}

function SuccessScreen({ token, jobTitle, t }) {
  return (
    <div className="py-16 flex flex-col items-center gap-6 text-center">
      <div
        className="w-16 h-16 rounded-full flex items-center justify-center text-2xl"
        style={{ backgroundColor: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.25)" }}
      >
        ✓
      </div>
      <div className="space-y-2">
        <h2 className="text-xl font-semibold" style={{ color: "var(--lm-ink)" }}>
          {t("careers.detail.successTitle")}
        </h2>
        <p className="text-sm" style={{ color: "var(--lm-ink-2)" }}>
          {t("careers.detail.successTextPre")}
          <strong>{jobTitle}</strong>
          {t("careers.detail.successTextPost")}
        </p>
      </div>
      <Link to={`/careers/track/${token}`} className="text-sm no-underline font-medium" style={{ color: "#5e6ad2" }}>
        {t("careers.detail.trackLink")}
      </Link>
    </div>
  );
}
